// Tiny inline-SVG donut chart for the per-month summary cards.
// No external chart library — Chart.js would add ~70 KB gz and we only
// need one shape, so a hand-rolled donut keeps the bundle small AND
// the CSP trivial (no eval / no separate scripts).
//
// Each segment is drawn as a separate stroked <circle> on the same
// center+radius. stroke-dasharray makes each circle render only its own
// arc; stroke-dashoffset positions each segment after the previous one.
// The whole SVG is rotated -90deg so segment 0 starts at 12 o'clock
// rather than 3 o'clock.

/** Per-segment fraction + cumulative offset for the dasharray/dashoffset
 *  attributes. Exported so unit tests can verify the math. */
export interface DonutSegment {
  name: string;
  value: number;
  /** Fraction of the whole, in [0, 1]. */
  fraction: number;
  /** Cumulative fraction at the START of this segment, in [0, 1). */
  cumOffset: number;
  color: string;
}

/** Stable category-name → HSL hue, mirroring the desktop dashboard's
 *  categoryColor() function. Same input → same color across renders, even
 *  if the order of categories shifts. */
export function categoryColor(name: string, alpha = 1): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsla(${hue}, 60%, 55%, ${alpha})`;
}

/**
 * Convert a {category: amount} map to a sorted segment list. Top N
 * categories get individual segments; the rest get bucketed into
 * "Other". Skips zero/negative values (the donut is expense-only).
 */
export function buildDonutSegments(
  byCategory: Record<string, number>,
  topN = 6,
): DonutSegment[] {
  const entries = Object.entries(byCategory)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return [];

  // Bucket the long tail into "Other". When there are <= topN entries
  // we render them all individually; no bucketing.
  let display: Array<[string, number]>;
  if (entries.length <= topN) {
    display = entries;
  } else {
    const top = entries.slice(0, topN);
    const rest = entries.slice(topN);
    const otherTotal = rest.reduce((s, [, v]) => s + v, 0);
    display = otherTotal > 0 ? [...top, ["Other", otherTotal]] : top;
  }

  const total = display.reduce((s, [, v]) => s + v, 0);
  if (total <= 0) return [];

  const segments: DonutSegment[] = [];
  let cum = 0;
  for (const [name, value] of display) {
    const fraction = value / total;
    segments.push({
      name,
      value,
      fraction,
      cumOffset: cum,
      color: categoryColor(name),
    });
    cum += fraction;
  }
  return segments;
}

interface DonutOptions {
  size?: number;
  strokeWidth?: number;
}

/** Build an SVG element rendering the donut. Returns null when there's
 *  no data so the caller can hide the chart cleanly. */
export function renderDonut(
  segments: DonutSegment[],
  opts: DonutOptions = {},
): SVGSVGElement | null {
  if (segments.length === 0) return null;
  const size = opts.size ?? 140;
  const strokeWidth = opts.strokeWidth ?? 22;
  const r = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
  svg.setAttribute("class", "donut");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", "Spending by category");

  // Background ring so empty fractions don't show as harsh gaps. Light
  // border color from the brand tokens.
  const bg = document.createElementNS(NS, "circle");
  bg.setAttribute("cx", String(cx));
  bg.setAttribute("cy", String(cy));
  bg.setAttribute("r", String(r));
  bg.setAttribute("fill", "transparent");
  bg.setAttribute("stroke", "rgba(0,0,0,0.06)");
  bg.setAttribute("stroke-width", String(strokeWidth));
  svg.appendChild(bg);

  for (const seg of segments) {
    const arc = document.createElementNS(NS, "circle");
    arc.setAttribute("cx", String(cx));
    arc.setAttribute("cy", String(cy));
    arc.setAttribute("r", String(r));
    arc.setAttribute("fill", "transparent");
    arc.setAttribute("stroke", seg.color);
    arc.setAttribute("stroke-width", String(strokeWidth));
    // dasharray = "<segment-length> <skip-rest-of-ring>". The rest is
    // larger than the circumference so the second dash never starts.
    arc.setAttribute(
      "stroke-dasharray",
      `${seg.fraction * circumference} ${circumference}`,
    );
    // Negative offset moves the dash clockwise; cumOffset is at the
    // START of this segment so subtracting it places the segment after
    // the prior ones.
    arc.setAttribute(
      "stroke-dashoffset",
      String(-(seg.cumOffset * circumference)),
    );
    arc.setAttribute("transform", `rotate(-90 ${cx} ${cy})`);
    // Tooltip for hover (browsers render SVG <title> as a native tooltip).
    const title = document.createElementNS(NS, "title");
    title.textContent = `${seg.name}: ${seg.value.toFixed(2)} (${Math.round(
      seg.fraction * 100,
    )}%)`;
    arc.appendChild(title);
    svg.appendChild(arc);
  }
  return svg;
}
